import { Component, computed, inject, input, model, signal } from '@angular/core';
import { ColDef, GridOptions } from 'ag-grid-community';
import { uniqBy } from 'lodash';
import { finalize } from 'rxjs';
import { defaultTableDefOptions } from '../../../../helpers';
import { BranchService } from '../../../../service/branch.service';
import { BranchCameraWithCameras } from '../../../../types';
import { CellInstallerDashboardCameraProviderActionsComponent } from '../cell-installer-dashboard-camera-provider-actions/cell-installer-dashboard-camera-provider-actions.component';

@Component({
  selector: 'app-installer-dashboard-table-providers',
  standalone: false,
  templateUrl: './installer-dashboard-table-providers.component.html',
  styleUrl: './installer-dashboard-table-providers.component.scss',
})
export class InstallerDashboardTableProvidersComponent {
  private branchService = inject(BranchService);

  public branchId = input.required<number>();
  public allCameraProviders = input.required<BranchCameraWithCameras[]>();
  public currentCameraProviders = model.required<BranchCameraWithCameras[]>();

  public newCameraProvider = signal<BranchCameraWithCameras | undefined>(undefined);
  public isHandlingProviders = signal<boolean>(false);

  public cameraAliases = computed(() => uniqBy(this.currentCameraProviders(), (p) => p.provider_branch_id));

  public readonly cameraProviderTable: GridOptions<BranchCameraWithCameras> = {
    ...defaultTableDefOptions(),
  };

  public readonly cameraProviderColumns: ColDef[] = [
    {
      field: 'client_name',
      headerName: 'Provider Name',
      cellDataType: 'text',
      minWidth: 200,
      filter: 'agTextColumnFilter',
      sortable: true,
      sort: 'asc',
    },
    {
      field: 'provider_branch_id',
      headerName: 'Provider Branch ID',
      cellDataType: 'text',
      minWidth: 200,
      filter: 'agTextColumnFilter',
      sortable: true,
    },
    {
      field: 'camera_provider',
      headerName: 'Camera Provider',
      cellDataType: 'text',
      minWidth: 200,
      filter: 'agTextColumnFilter',
      sortable: true,
    },
    {
      field: 'actions',
      headerName: '',
      suppressColumnsToolPanel: true,
      suppressKeyboardEvent: () => true,
      sortable: false,
      width: 70,
      maxWidth: 70,
      cellClass: ['inline-flex', 'items-center', 'justify-end'],
      cellRenderer: CellInstallerDashboardCameraProviderActionsComponent,
      cellRendererParams: {
        deleteCallback: (item: BranchCameraWithCameras) => {
          this.removeCameraProvider(item);
        },
      },
    },
  ];

  addCameraProvider() {
    const newCameraProvider = this.newCameraProvider();
    this.isHandlingProviders.set(true);
    this.branchService
      .addBranchCameraProvider(this.branchId(), newCameraProvider.camera_provider, newCameraProvider.provider_branch_id)
      .pipe(finalize(() => this.isHandlingProviders.set(false)))
      .subscribe((createdProvider) => {
        this.newCameraProvider.set(undefined);
        this.currentCameraProviders.set([...this.currentCameraProviders(), createdProvider]);
      });
  }

  removeCameraProvider(item: BranchCameraWithCameras) {
    this.isHandlingProviders.set(true);
    this.branchService
      .removeBranchCameraProvider(this.branchId(), item.id)
      .pipe(finalize(() => this.isHandlingProviders.set(false)))
      .subscribe(() => {
        this.currentCameraProviders.set(this.currentCameraProviders().filter((f) => f.id !== item.id));
      });
  }
}
